import { supabase } from '../lib/supabase';
import type {
  FilterGroup,
  HeatmapSavedFilter,
  SystemPreset,
} from '../types/filter';
import { generateId } from '../types/filter';

const LOCAL_STORAGE_PRESETS_PREFIX = 'heatmap_saved_filters_';
const LOCAL_STORAGE_ACTIVE_PREFIX = 'heatmap_active_filter_';

// ── Built-in System Presets ───────────────────────────────────

export function getSystemPresets(): SystemPreset[] {
  return [
    {
      id: 'system_nicht_geoeffnet',
      name: 'Nicht geöffnet (Re-Visits)',
      description: 'Adressen mit Kunden, die beim Erstbesuch nicht angetroffen wurden.',
      isSystem: true,
      filter_config: {
        id: 'sys_group_1',
        type: 'group',
        conjunction: 'and',
        children: [
          {
            id: 'sys_cond_1',
            type: 'condition',
            field: 'interest_level',
            operator: 'is',
            value: 'Nicht geöffnet',
          },
        ],
      },
    },
    {
      id: 'system_neukunden_30d',
      name: 'Neukunden letzte 30 Tage',
      description: 'Erfolgreich gewonnene Kunden der vergangenen 30 Tage.',
      isSystem: true,
      filter_config: {
        id: 'sys_group_2',
        type: 'group',
        conjunction: 'and',
        children: [
          {
            id: 'sys_cond_2a',
            type: 'condition',
            field: 'interest_level',
            operator: 'is',
            value: 'Contract',
          },
          {
            id: 'sys_cond_2b',
            type: 'condition',
            field: 'visit_date',
            operator: 'is_past_30_days',
            value: null,
          },
        ],
      },
    },
    {
      id: 'system_senioren_60plus',
      name: 'Seniorenverträge (60+)',
      description: 'Kunden ab 60 Jahren mit abgeschlossenem Vertrag.',
      isSystem: true,
      filter_config: {
        id: 'sys_group_3',
        type: 'group',
        conjunction: 'and',
        children: [
          {
            id: 'sys_cond_3a',
            type: 'condition',
            field: 'interest_level',
            operator: 'is',
            value: 'Contract',
          },
          {
            id: 'sys_cond_3b',
            type: 'condition',
            field: 'customer_age',
            operator: 'greater_than_or_equal',
            value: 60,
          },
        ],
      },
    },
    {
      id: 'system_strom_vertraege',
      name: 'Stromverträge',
      description: 'Kundenstandorte mit Verträgen in der Kategorie Strom.',
      isSystem: true,
      filter_config: {
        id: 'sys_group_4',
        type: 'group',
        conjunction: 'and',
        children: [
          {
            id: 'sys_cond_4',
            type: 'condition',
            field: 'contract_category',
            operator: 'is',
            value: 'Strom',
          },
        ],
      },
    },
  ];
}

// ── LocalStorage Helpers ──────────────────────────────────────

function getLocalStoragePresets(userId: string): HeatmapSavedFilter[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(`${LOCAL_STORAGE_PRESETS_PREFIX}${userId}`);
    if (!raw) return [];
    return JSON.parse(raw);
  } catch (err) {
    console.warn('Error reading presets from localStorage:', err);
    return [];
  }
}

function setLocalStoragePresets(userId: string, presets: HeatmapSavedFilter[]): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      `${LOCAL_STORAGE_PRESETS_PREFIX}${userId}`,
      JSON.stringify(presets)
    );
  } catch (err) {
    console.warn('Error saving presets to localStorage:', err);
  }
}

// ── DB & Hybrid Presets Service ───────────────────────────────

/**
 * Loads saved presets for a user from Supabase DB with fallback to localStorage.
 */
export async function getSavedPresets(userId: string): Promise<HeatmapSavedFilter[]> {
  try {
    const { data, error } = await supabase
      .from('heatmap_saved_filters' as any)
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (!error && Array.isArray(data)) {
      const presets: HeatmapSavedFilter[] = data.map((row: any) => ({
        id: row.id,
        user_id: row.user_id,
        name: row.name,
        description: row.description || null,
        filter_config: row.filter_config,
        is_default: Boolean(row.is_default),
        created_at: row.created_at,
        updated_at: row.updated_at,
      }));

      // Mirror to localStorage for offline fallback
      setLocalStoragePresets(userId, presets);
      return presets;
    }

    if (error) {
      console.warn(
        'Supabase heatmap_saved_filters table not available or errored, using localStorage fallback:',
        error.message
      );
    }
  } catch (err) {
    console.warn('Failed to query Supabase for presets, falling back to localStorage:', err);
  }

  return getLocalStoragePresets(userId);
}

/**
 * Saves a new preset to the database and mirrors it to localStorage.
 */
export async function savePreset(
  userId: string,
  name: string,
  description: string | null,
  filterConfig: FilterGroup,
  isDefault = false
): Promise<HeatmapSavedFilter> {
  const newPreset: HeatmapSavedFilter = {
    id: generateId(),
    user_id: userId,
    name,
    description: description || null,
    filter_config: filterConfig,
    is_default: isDefault,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  // If new preset is default, update existing presets locally and in DB
  if (isDefault) {
    try {
      await supabase
        .from('heatmap_saved_filters' as any)
        .update({ is_default: false })
        .eq('user_id', userId);
    } catch {
      // ignore table missing error
    }
  }

  let dbSavedPreset: HeatmapSavedFilter = newPreset;

  try {
    const { data, error } = await supabase
      .from('heatmap_saved_filters' as any)
      .insert({
        user_id: userId,
        name,
        description: description || null,
        filter_config: filterConfig,
        is_default: isDefault,
      })
      .select()
      .single();

    if (!error && data) {
      dbSavedPreset = {
        id: (data as any).id,
        user_id: (data as any).user_id,
        name: (data as any).name,
        description: (data as any).description || null,
        filter_config: (data as any).filter_config,
        is_default: Boolean((data as any).is_default),
        created_at: (data as any).created_at,
        updated_at: (data as any).updated_at,
      };
    }
  } catch (err) {
    console.warn('Could not insert preset to Supabase, saving only locally:', err);
  }

  // Update local storage
  const current = getLocalStoragePresets(userId);
  const updated = isDefault
    ? current.map((p) => ({ ...p, is_default: false }))
    : [...current];
  updated.unshift(dbSavedPreset);
  setLocalStoragePresets(userId, updated);

  return dbSavedPreset;
}

/**
 * Deletes a preset by ID from DB and localStorage.
 */
export async function deletePreset(userId: string, presetId: string): Promise<void> {
  try {
    await supabase
      .from('heatmap_saved_filters' as any)
      .delete()
      .eq('id', presetId)
      .eq('user_id', userId);
  } catch (err) {
    console.warn('Could not delete preset from Supabase:', err);
  }

  const current = getLocalStoragePresets(userId);
  const filtered = current.filter((p) => p.id !== presetId);
  setLocalStoragePresets(userId, filtered);
}

/**
 * Sets a preset as the default preset for the user.
 */
export async function setDefaultPreset(userId: string, presetId: string): Promise<void> {
  try {
    // 1. Clear all defaults for user
    await supabase
      .from('heatmap_saved_filters' as any)
      .update({ is_default: false })
      .eq('user_id', userId);

    // 2. Set this preset as default
    await supabase
      .from('heatmap_saved_filters' as any)
      .update({ is_default: true })
      .eq('id', presetId)
      .eq('user_id', userId);
  } catch (err) {
    console.warn('Could not update default preset in Supabase:', err);
  }

  const current = getLocalStoragePresets(userId);
  const updated = current.map((p) => ({
    ...p,
    is_default: p.id === presetId,
  }));
  setLocalStoragePresets(userId, updated);
}

// ── Active Draft State Persistence ────────────────────────────

/**
 * Saves current uncommitted draft filter state in localStorage so it persists
 * across page refreshes or tab navigation.
 */
export function saveActiveFilterState(userId: string, filterConfig: FilterGroup | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (!filterConfig) {
      localStorage.removeItem(`${LOCAL_STORAGE_ACTIVE_PREFIX}${userId}`);
    } else {
      localStorage.setItem(
        `${LOCAL_STORAGE_ACTIVE_PREFIX}${userId}`,
        JSON.stringify(filterConfig)
      );
    }
  } catch (err) {
    console.warn('Error saving active filter state:', err);
  }
}

/**
 * Retrieves the last active draft filter state from localStorage.
 */
export function getActiveFilterState(userId: string): FilterGroup | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`${LOCAL_STORAGE_ACTIVE_PREFIX}${userId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn('Error reading active filter state:', err);
    return null;
  }
}

export function clearActiveFilterState(userId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(`${LOCAL_STORAGE_ACTIVE_PREFIX}${userId}`);
  } catch (err) {
    console.warn('Error clearing active filter state:', err);
  }
}
